/* Some mappings for cargo units defined in database (public.cargo_units) */

import { CUBIC_METER_IN_CUBIC_FEET } from './VolumeUnits';
import { PaymentUnit } from '@seaber/common/graphql/types';
import type { TimeData } from '../components/core/TimeData';
export const LONG_TON_IN_METRIC_TONS = 1.016047;
export const SHORT_TON_IN_METRIC_TONS = 0.907185;
export const POUND_IN_METRIC_TONS = 0.00045359237;

const massCargoUnits = ['t', 'admt', 'mts', 'bdmt', 'lt', 'kg', 'st'] as const;
const volumeCargoUnits = ['m3', 'ft3', 'cbm', 'scbm ub', 'scbm ob'] as const;

export const reducedUnits = ['mts', 'm3', 'ft3', 'pcs', 'set', 'frt'];

export type PhysicalQuantity = 'mass' | 'volume';

export type MetricTonnes = number & { __volumeUnitBrand: 'mts' };
export type CubicMeters = number & { __volumeUnitBrand: 'm3' };

/** Returns which physical quantity the cargo unit represents */
export function getCargoUnitQuantity(
  cargoUnitName: string,
): PhysicalQuantity | null {
  if (
    massCargoUnits.includes(cargoUnitName as (typeof massCargoUnits)[number])
  ) {
    return 'mass';
  }

  if (
    volumeCargoUnits.includes(
      cargoUnitName as (typeof volumeCargoUnits)[number],
    )
  ) {
    return 'volume';
  }

  return null;
}

/** Convert stowage factor from ft³/LT to m³/t */
export function convertStowageFactorToMetric(stowageFactor: number) {
  return stowageFactor / CUBIC_METER_IN_CUBIC_FEET / LONG_TON_IN_METRIC_TONS;
}

/**
 * Determines which physical quantity FRT (Freight Rate Ton) represents
 * based on stowage factor or specific gravity.
 *
 * FRT = max(MT, CBM) where 1 MT = 1 CBM.
 * Threshold is 1 m³/t (metric), ~35.88 ft³/LT.
 *
 * Priority matches calculateMassAndVolumeRanges in Capacity.ts:
 * - SOLID: SF first, SG fallback
 * - LIQUID/GAS: SG first, SF fallback
 * - Default cargo type: SOLID
 */
export function getFrtQuantity(
  stowageFactorMin?: number | null,
  stowageFactorMax?: number | null,
  specificGravity?: number | null,
  cargoType?: string | null,
): PhysicalQuantity {
  const sf = stowageFactorMin ?? stowageFactorMax;
  const hasSf = sf != null;
  const hasSg = specificGravity != null && specificGravity !== 0;

  const sfResult = (): PhysicalQuantity => {
    const sfMin = stowageFactorMin ?? sf!;
    const sfMax = stowageFactorMax ?? sf!;
    const metricSf = convertStowageFactorToMetric((sfMin + sfMax) / 2);
    return metricSf >= 1 ? 'volume' : 'mass';
  };

  const sgResult = (): PhysicalQuantity =>
    specificGravity! <= 1 ? 'volume' : 'mass';

  const isLiquidOrGas = cargoType === 'LIQUID' || cargoType === 'GAS';

  if (isLiquidOrGas) {
    if (hasSg) return sgResult();
    if (hasSf) return sfResult();
  } else {
    if (hasSf) return sfResult();
    if (hasSg) return sgResult();
  }

  return 'mass';
}

/**
 * Resolves which physical quantity a cargo unit represents, handling FRT
 * by using stowage factor / specific gravity from the assortment.
 */
export function resolveCargoUnitQuantity(
  cargoUnitName: string,
  assortment?: {
    stowageFactorMin?: string | number | null;
    stowageFactorMax?: string | number | null;
    specificGravity?: string | number | null;
    cargoType?: string | null;
  } | null,
): PhysicalQuantity {
  if (cargoUnitName === 'frt') {
    const sfMin =
      assortment?.stowageFactorMin != null
        ? Number(assortment.stowageFactorMin)
        : null;
    const sfMax =
      assortment?.stowageFactorMax != null
        ? Number(assortment.stowageFactorMax)
        : null;
    const sg =
      assortment?.specificGravity != null
        ? Number(assortment.specificGravity)
        : null;
    return getFrtQuantity(sfMin, sfMax, sg, assortment?.cargoType);
  }
  return getCargoUnitQuantity(cargoUnitName) ?? 'mass';
}

/**
 * Returns the display cargo amount for a cargo event.
 * FRT: order amount scaled by td.amount (0..1) to only show less than input value when the full amount doesnt fit.
 * Other units: derived from td.mass/td.volume and converted back to the cargo unit.
 */
export function getDisplayCargoAmount({
  td,
  cargoUnit,
  orderCargoAmount,
}: {
  td: Pick<TimeData, 'mass' | 'volume' | 'amount'>;
  cargoUnit: string;
  orderCargoAmount: string | number;
}): number {
  if (cargoUnit === 'frt') {
    return Number(orderCargoAmount) * td.amount;
  }
  return getCargoUnitQuantity(cargoUnit) === 'volume'
    ? systemAmountToCargoUnit(Math.abs(td.volume), cargoUnit)
    : systemAmountToCargoUnit(Math.abs(td.mass), cargoUnit);
}

/** Returns cargo amount in mt/m3, very naive implementation */
export function cargoAmountToSystemUnit(
  amount: number,
  cargoUnitName: string,
): number {
  switch (cargoUnitName) {
    case 'mts':
    case 'admt':
    case 'bdmt':
    case 't': {
      return amount;
    }
    case 'kg': {
      return amount / 1000;
    }
    case 'scbm ub':
    case 'scbm ob':
    case 'lcbm':
    case 'cbm':
    case 'm3': {
      return amount;
    }
    case 'ft3': {
      return amount / CUBIC_METER_IN_CUBIC_FEET;
    }
    case 'lt': {
      return amount * LONG_TON_IN_METRIC_TONS;
    }
    case 'st': {
      return amount * SHORT_TON_IN_METRIC_TONS;
    }
    case 'set':
    case 'pcs': {
      return amount;
    }
    case 'frt': {
      // FRT (Freight Rate Ton) = max(mass, volume). Without SF/SG context,
      // assume metric SF=1 so FRT equals both MT and M3.
      return amount;
    }
    default: {
      console.error(
        'cargoAmountToSystemUnit: No correct mapping found for cargo unit',
        cargoUnitName,
      );
      return amount;
    }
  }
}

/** Returns system cargo amount in given cargo units */
export function systemAmountToCargoUnit(
  amount: number,
  cargoUnitName: string,
): number {
  return amount / cargoAmountToSystemUnit(1, cargoUnitName);
}

/** Maps a cargo unit name to its corresponding payment unit */
export function cargoUnitToPaymentUnit(
  cargoUnit: string | null | undefined,
): PaymentUnit {
  if (!cargoUnit) {
    return PaymentUnit.MT;
  }

  const unit = cargoUnit.toLowerCase();

  switch (unit) {
    case 'bdmt':
      return PaymentUnit.BDMT;
    case 't':
    case 'mts':
    case 'admt':
    case 'kg':
    case 'lt':
    case 'st':
      return PaymentUnit.MT;
    case 'm3':
    case 'cbm':
      return PaymentUnit.M3;
    case 'ft3':
      return PaymentUnit.FT3;
    case 'scbm ub':
      return PaymentUnit.SCBM_UB;
    case 'pcs':
      return PaymentUnit.PER_PCS;
    case 'set':
      return PaymentUnit.PER_SET;
    case 'frt':
      return PaymentUnit.FRT;
    default: {
      const quantity = getCargoUnitQuantity(unit);
      return quantity === 'volume'
        ? PaymentUnit.M3
        : quantity === 'mass'
          ? PaymentUnit.MT
          : unit === 'set'
            ? PaymentUnit.PER_SET
            : PaymentUnit.PER_PCS;
    }
  }
}
